import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import withRouter from "components/utils/withRouter.js";
import styles from "styles/public.module.css";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackbar } from "notistack";
import SnackbarConstants from "utils/constants/Snackbar.js";
import SignupService from "services/SignupService.js";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import RegexUtils from "utils/RegexUtils.js";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

function PatientSignup(props) {
  const { enqueueSnackbar } = useSnackbar();
  const [signupViewState, setSignupViewState] = useState(0);
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showOtpTimer, setShowOtpTimer] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(30);
  let timer;

  useEffect(() => {
    // Cleanup the timer when the component unmounts
    return () => {
      stopOtpTimer();
    };
  }, []);

  const {
    register,
    formState: { errors },
    getValues,
    handleSubmit,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  async function handlePatientSignup(data) {
    setIsFormProcessing(true);
    if (signupViewState === 0) {
      try {
        const captchaToken = await executeRecaptcha("Register_User");
        const response = await SignupService.registerPatient(
          {
            email: data.email,
          },
          captchaToken
        );

        // Check if user was added to organization (data is null)
        if (!response.data || response.data === null) {
          // User account already exists and has been added to the current organization
          enqueueSnackbar(response.message, SnackbarConstants.success);
          // Redirect to login page after showing the message
          setTimeout(() => {
            props.navigate("/account");
          }, 2000);
          setIsFormProcessing(false);
          return;
        }

        // Normal registration flow - continue with OTP verification
        setTransactionId(response.data.transactionId);
        setSignupViewState(1);
        startOtpTimer();
        enqueueSnackbar(
          "Verify your account by entering the OTP sent to your email",
          SnackbarConstants.success
        );
      } catch (error) {
        handleError(error);
      }
    } else if (signupViewState === 1) {
      try {
        const captchaToken = await executeRecaptcha("Verify_Registered_User");
        const response = await SignupService.verifyRegisteredPatient(
          {
            email: data.email,
            otp: data.otp,
            transactionId,
          },
          captchaToken
        );
        setTransactionId(response.data.transactionId);
        setSignupViewState(2);
        enqueueSnackbar(response.message, SnackbarConstants.success);
      } catch (error) {
        handleError(error);
      }
    } else {
      try {
        const captchaToken = await executeRecaptcha("Signup_User");
        const response = await SignupService.signupPatient(
          {
            ...data,
            transactionId,
          },
          captchaToken
        );
        enqueueSnackbar(response.message, SnackbarConstants.success);
        props.navigate("/");
      } catch (error) {
        handleError(error);
      }
    }
    setIsFormProcessing(false);
  }

  function handleError(error) {
    let errorData = error.data;
    if (error.data) {
      enqueueSnackbar(errorData.message, SnackbarConstants.error);
      let errors = errorData.errors;
      if (errors) {
        errors.forEach((item, i) => {
          enqueueSnackbar(item, SnackbarConstants.error);
        });
      }
    } else {
      enqueueSnackbar("Oops! Something went wrong", SnackbarConstants.error);
    }
    setIsFormProcessing(false);
  }

  async function resendOTP() {
    setIsFormProcessing(true);
    startOtpTimer();

    try {
      const captchaToken = await executeRecaptcha("Resend_OTP");

      const data = getValues();

      const response = await SignupService.resendOTP(
        {
          email: data.email,
        },
        captchaToken
      );
      setTransactionId(response.data.transactionId);
      enqueueSnackbar("OTP resent", SnackbarConstants.success);
    } catch (error) {
      stopOtpTimer();
      handleError(error);
    }
    setIsFormProcessing(false);
  }

  function startOtpTimer() {
    setShowOtpTimer(true);
    setTimerSeconds(30);

    timer = setInterval(() => {
      setTimerSeconds((prevSeconds) => {
        if (prevSeconds == 0) {
          stopOtpTimer();
        }
        // Decrement the seconds by 1
        return prevSeconds - 1;
      });
    }, 1000); // Update every 1000ms (1 second)
  }

  function stopOtpTimer() {
    setShowOtpTimer(false);
    clearInterval(timer);
  }

  return (
    <Paper elevation={0} className={styles["form-container"]}>
      <h4>Register as a Patient</h4>
      <form className="mt-4" onSubmit={handleSubmit(handlePatientSignup)}>
        <div className="mb-3">
          <label htmlFor="emailInput" className="form-label">
            Email ID
          </label>
          <input
            type="email"
            name="email"
            className="form-control"
            id="emailInput"
            aria-describedby="emailHelp"
            {...register("email", {
              required: "Email ID is required",
              pattern: {
                value: RegexUtils.EMAIL,
                message: "Email ID is invalid",
              },
            })}
          />
          <InputErrorMessage name="email" errors={errors} />
        </div>
        {signupViewState === 1 ? (
          <>
            <div className="mb-3">
              <label htmlFor="otpInput" className="form-label">
                OTP
              </label>
              <input
                type="number"
                name="otp"
                className="form-control"
                id="otpInput"
                {...register("otp", {
                  required: "OTP is required",
                  pattern: {
                    value: RegexUtils.OTP,
                    message: "OTP is invalid",
                  },
                })}
              />
              <InputErrorMessage name="otp" errors={errors} />
            </div>
            <div className="text-end mb-2">
              {showOtpTimer ? (
                <small>Resend OTP in {timerSeconds} s</small>
              ) : (
                <Link component="button" onClick={resendOTP} underline="none">
                  Resend OTP
                </Link>
              )}
            </div>
          </>
        ) : null}
        {signupViewState === 2 ? (
          <div>
            <div className="mb-3">
              <label htmlFor="firstNameInput" className="form-label">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                className="form-control"
                id="firstNameInput"
                {...register("firstName", {
                  required: "First Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "First Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="firstName" errors={errors} />
            </div>
            <div className="mb-3">
              <label htmlFor="lastNameInput" className="form-label">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                className="form-control"
                id="lastNameInput"
                {...register("lastName", {
                  required: "Last Name is required",
                  pattern: {
                    value: RegexUtils.NAME,
                    message: "Last Name is invalid",
                  },
                })}
              />
              <InputErrorMessage name="lastName" errors={errors} />
            </div>
            <div className="mb-3">
              <label htmlFor="passwordInput" className="form-label">
                Password
              </label>
              <div>
                <OutlinedInput
                  name="password"
                  id="passwordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      {" "}
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((show) => !show)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("password", {
                    required: "Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "Password is invalid",
                    },
                  })}
                />
              </div>
              <InputErrorMessage name="password" errors={errors} />
              <div id="passwordHelpBlock" className="form-text">
                Password must be atleast 8 characters long, including atleast
                one letter, one number, and one special character
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="confirmPasswordInput" className="form-label">
                Confirm Password
              </label>
              <div>
                <OutlinedInput
                  name="confirmPassword"
                  id="confirmPasswordInput"
                  className={styles["password-input"]}
                  autoComplete="new-password"
                  type={showConfirmPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      {" "}
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword((show) => !show)}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...register("confirmPassword", {
                    required: "Confirm Password is required",
                    pattern: {
                      value: RegexUtils.PASSWORD,
                      message: "Confirm Password is invalid",
                    },
                  })}
                />
              </div>
              <InputErrorMessage name="confirmPassword" errors={errors} />
            </div>
          </div>
        ) : null}
        {isFormProcessing ? (
          <LoadingButton loading variant="contained" className="me-3">
            {getSubmitButtonText()}
          </LoadingButton>
        ) : (
          <Button type="submit" className="me-3" variant="contained">
            {getSubmitButtonText()}
          </Button>
        )}
        <Button onClick={() => props.navigate(0)} variant="outlined">
          Cancel
        </Button>
      </form>
    </Paper>
  );

  function getSubmitButtonText() {
    if (signupViewState === 0) return "Submit";
    if (signupViewState === 1) return "Verify";
    return "Register";
  }
}

export default withRouter(PatientSignup);
